import { createSeededNoise } from "./perlinNoise";

export type DisplacementMode = "noise" | "image";
export type ChannelMode = "rg" | "luminance";

/** Path warp when `mode` is `"noise"` (ignored for image maps). */
export type NoiseDisplacementStyle =
    | "fbm"
    | "swirl"
    | "ripple"
    | "curl"
    | "domain-warp"
    | "twist"
    | "shatter"
    | "barrel";

export const NOISE_DISPLACEMENT_STYLE_OPTIONS: ReadonlyArray<{
    value: NoiseDisplacementStyle;
    label: string;
}> = [
    { value: "fbm", label: "FBM (classic X/Y)" },
    { value: "swirl", label: "Swirl (tangential)" },
    { value: "ripple", label: "Ripple (radial)" },
    { value: "curl", label: "Curl noise (flow)" },
    { value: "domain-warp", label: "Domain warp (organic)" },
    { value: "twist", label: "Twist (pinwheel)" },
    { value: "shatter", label: "Shatter (jagged)" },
    { value: "barrel", label: "Barrel (lens)" },
];

export interface DisplacementParams {
    strength: number;
    circleSegments: number;
    mode: DisplacementMode;
    // Noise mode
    seed: string;
    noiseScale: number;
    octaves: number;
    /** Defaults to `"fbm"` (classic independent X/Y FBM). */
    noiseStyle?: NoiseDisplacementStyle;
    // Image mode
    imageData: ImageData | null;
    channelMode: ChannelMode;
    svgMinX: number;
    svgMinY: number;
    svgWidth: number;
    svgHeight: number;
    /**
     * When true, each matched shape (path, rect, …) gets its own noise seed
     * (`seed` + stable index). Default false: one field for the whole SVG.
     */
    perShapeSeededNoise?: boolean;
}

export type DisplaceFn = (x: number, y: number) => { x: number; y: number };

// ─── FBM ────────────────────────────────────────────────────────────────────

function fbm(
    noiseFn: (x: number, y: number) => number,
    x: number,
    y: number,
    octaves: number,
): number {
    let value = 0;
    let amplitude = 0.5;
    let frequency = 1;
    for (let i = 0; i < octaves; i++) {
        value += amplitude * noiseFn(x * frequency, y * frequency);
        amplitude *= 0.5;
        frequency *= 2;
    }
    return value;
}

// ─── Noise-based displace functions ─────────────────────────────────────────

function makeFbmDisplaceFn(params: DisplacementParams): DisplaceFn {
    const { seed, noiseScale, strength, octaves } = params;
    const noiseX = createSeededNoise(seed + "_x");
    const noiseY = createSeededNoise(seed + "_y");
    return (x: number, y: number) => {
        const nx = x * noiseScale;
        const ny = y * noiseScale;
        const dx = (fbm(noiseX, nx, ny, octaves) - 0.5) * 2 * strength;
        const dy = (fbm(noiseY, nx, ny, octaves) - 0.5) * 2 * strength;
        return { x: x + dx, y: y + dy };
    };
}

/** Tangential warp around viewBox center; magnitude from FBM. */
function makeSwirlNoiseDisplaceFn(params: DisplacementParams): DisplaceFn {
    const { seed, noiseScale, strength, octaves, svgMinX, svgMinY, svgWidth, svgHeight } = params;
    const noiseMag = createSeededNoise(seed + "_swirl");
    const cx = svgMinX + svgWidth * 0.5;
    const cy = svgMinY + svgHeight * 0.5;
    return (x: number, y: number) => {
        const rx = x - cx;
        const ry = y - cy;
        const dist = Math.hypot(rx, ry);
        if (dist < 1e-9) return { x, y };
        const tx = -ry / dist;
        const ty = rx / dist;
        const nx = x * noiseScale;
        const ny = y * noiseScale;
        const mag = (fbm(noiseMag, nx, ny, octaves) - 0.5) * 2 * strength;
        return { x: x + tx * mag, y: y + ty * mag };
    };
}

/** Radial push/pull with ring-like phase; phase jitter from FBM. */
function makeRippleNoiseDisplaceFn(params: DisplacementParams): DisplaceFn {
    const { seed, noiseScale, strength, octaves, svgMinX, svgMinY, svgWidth, svgHeight } = params;
    const noisePhase = createSeededNoise(seed + "_ripple");
    const cx = svgMinX + svgWidth * 0.5;
    const cy = svgMinY + svgHeight * 0.5;
    const diag = Math.max(1e-6, Math.hypot(svgWidth, svgHeight));
    return (x: number, y: number) => {
        const rx = x - cx;
        const ry = y - cy;
        const dist = Math.hypot(rx, ry);
        if (dist < 1e-9) return { x, y };
        const ux = rx / dist;
        const uy = ry / dist;
        const nx = x * noiseScale;
        const ny = y * noiseScale;
        const rings = Math.max(1, 3 * octaves * (0.25 + noiseScale * diag * 0.02));
        const phase = (dist / diag) * Math.PI * 2 * rings;
        const jitter = (fbm(noisePhase, nx, ny, octaves) - 0.5) * Math.PI;
        const wobble = Math.sin(phase + jitter) * strength * 0.45;
        return { x: x + ux * wobble, y: y + uy * wobble };
    };
}

/**
 * Curl noise — divergence-free displacement.
 * Uses the numerical gradient of a scalar noise field and rotates it 90°
 * so displacement follows "flow lines" without compressing/expanding space.
 */
function makeCurlDisplaceFn(params: DisplacementParams): DisplaceFn {
    const { seed, noiseScale, strength, octaves } = params;
    const noiseScalar = createSeededNoise(seed + "_curl");
    const eps = 0.5;
    return (x: number, y: number) => {
        const nx = x * noiseScale;
        const ny = y * noiseScale;
        const dndx =
            (fbm(noiseScalar, nx + eps, ny, octaves) -
                fbm(noiseScalar, nx - eps, ny, octaves)) /
            (2 * eps);
        const dndy =
            (fbm(noiseScalar, nx, ny + eps, octaves) -
                fbm(noiseScalar, nx, ny - eps, octaves)) /
            (2 * eps);
        // rotate gradient 90° → (dndy, -dndx)
        const dx = dndy * strength;
        const dy = -dndx * strength;
        return { x: x + dx, y: y + dy };
    };
}

/**
 * Domain warping — feeds noise into itself.
 * The input coordinates are offset by one noise field before being sampled
 * by a second, producing highly organic, almost geological distortion.
 */
function makeDomainWarpDisplaceFn(params: DisplacementParams): DisplaceFn {
    const { seed, noiseScale, strength, octaves } = params;
    const noiseA = createSeededNoise(seed + "_dwA");
    const noiseB = createSeededNoise(seed + "_dwB");
    const noiseC = createSeededNoise(seed + "_dwC");
    const noiseD = createSeededNoise(seed + "_dwD");
    return (x: number, y: number) => {
        const nx = x * noiseScale;
        const ny = y * noiseScale;
        const wx = (fbm(noiseA, nx, ny, octaves) - 0.5) * 4;
        const wy = (fbm(noiseB, nx, ny, octaves) - 0.5) * 4;
        const dx = (fbm(noiseC, nx + wx, ny + wy, octaves) - 0.5) * 2 * strength;
        const dy = (fbm(noiseD, nx + wx, ny + wy, octaves) - 0.5) * 2 * strength;
        return { x: x + dx, y: y + dy };
    };
}

/**
 * Twist — rotation angle proportional to distance from center.
 * Closer points barely move; far points rotate strongly, like a pinwheel.
 */
function makeTwistDisplaceFn(params: DisplacementParams): DisplaceFn {
    const { strength, svgMinX, svgMinY, svgWidth, svgHeight } = params;
    const cx = svgMinX + svgWidth * 0.5;
    const cy = svgMinY + svgHeight * 0.5;
    const diag = Math.max(1e-6, Math.hypot(svgWidth, svgHeight));
    const maxAngle = (strength / diag) * Math.PI * 2;
    return (x: number, y: number) => {
        const rx = x - cx;
        const ry = y - cy;
        const dist = Math.hypot(rx, ry);
        const angle = dist * maxAngle;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        return {
            x: cx + rx * cos - ry * sin,
            y: cy + rx * sin + ry * cos,
        };
    };
}

/**
 * Shatter — FBM with absolute value (turbulence), producing jagged, angular
 * displacement with hard creases where the noise crosses zero.
 */
function makeShatterDisplaceFn(params: DisplacementParams): DisplaceFn {
    const { seed, noiseScale, strength, octaves } = params;
    const noiseX = createSeededNoise(seed + "_shX");
    const noiseY = createSeededNoise(seed + "_shY");
    return (x: number, y: number) => {
        const nx = x * noiseScale;
        const ny = y * noiseScale;
        const rawX = (fbm(noiseX, nx, ny, octaves) - 0.5) * 2;
        const rawY = (fbm(noiseY, nx, ny, octaves) - 0.5) * 2;
        const dx = (Math.abs(rawX) * 2 - 0.5) * strength;
        const dy = (Math.abs(rawY) * 2 - 0.5) * strength;
        return { x: x + dx, y: y + dy };
    };
}

/**
 * Barrel — radial lens distortion.
 * Points move outward quadratically from center, modulated by FBM so it
 * isn't a perfectly smooth lens (adds organic wobble).
 */
function makeBarrelDisplaceFn(params: DisplacementParams): DisplaceFn {
    const { seed, noiseScale, strength, octaves, svgMinX, svgMinY, svgWidth, svgHeight } = params;
    const noiseMod = createSeededNoise(seed + "_barrel");
    const cx = svgMinX + svgWidth * 0.5;
    const cy = svgMinY + svgHeight * 0.5;
    const halfDiag = Math.max(1e-6, Math.hypot(svgWidth, svgHeight) * 0.5);
    return (x: number, y: number) => {
        const rx = x - cx;
        const ry = y - cy;
        const r = Math.hypot(rx, ry);
        if (r < 1e-9) return { x, y };
        const rn = r / halfDiag;
        const barrel = rn * rn;
        const nx = x * noiseScale;
        const ny = y * noiseScale;
        const mod = 0.5 + fbm(noiseMod, nx, ny, octaves);
        const scale = barrel * mod * (strength / halfDiag);
        return {
            x: x + rx * scale,
            y: y + ry * scale,
        };
    };
}

function makeNoiseDisplaceFn(params: DisplacementParams): DisplaceFn {
    const style = params.noiseStyle ?? "fbm";
    switch (style) {
        case "swirl":
            return makeSwirlNoiseDisplaceFn(params);
        case "ripple":
            return makeRippleNoiseDisplaceFn(params);
        case "curl":
            return makeCurlDisplaceFn(params);
        case "domain-warp":
            return makeDomainWarpDisplaceFn(params);
        case "twist":
            return makeTwistDisplaceFn(params);
        case "shatter":
            return makeShatterDisplaceFn(params);
        case "barrel":
            return makeBarrelDisplaceFn(params);
        case "fbm":
        default:
            return makeFbmDisplaceFn(params);
    }
}

// ─── Image-based displace function ──────────────────────────────────────────

/** Bilinear sample of an ImageData at normalized [0,1] UV coordinates. */
function sampleBilinear(
    imageData: ImageData,
    u: number,
    v: number,
): { r: number; g: number; b: number } {
    const w = imageData.width;
    const h = imageData.height;
    const px = Math.max(0, Math.min(1, u)) * (w - 1);
    const py = Math.max(0, Math.min(1, v)) * (h - 1);
    const x0 = Math.floor(px);
    const y0 = Math.floor(py);
    const x1 = Math.min(x0 + 1, w - 1);
    const y1 = Math.min(y0 + 1, h - 1);
    const fx = px - x0;
    const fy = py - y0;

    const getPixel = (px2: number, py2: number) => {
        const idx = (py2 * w + px2) * 4;
        return {
            r: imageData.data[idx]     / 255,
            g: imageData.data[idx + 1] / 255,
            b: imageData.data[idx + 2] / 255,
        };
    };

    const tl = getPixel(x0, y0);
    const tr = getPixel(x1, y0);
    const bl = getPixel(x0, y1);
    const br = getPixel(x1, y1);

    return {
        r: tl.r * (1 - fx) * (1 - fy) + tr.r * fx * (1 - fy) + bl.r * (1 - fx) * fy + br.r * fx * fy,
        g: tl.g * (1 - fx) * (1 - fy) + tr.g * fx * (1 - fy) + bl.g * (1 - fx) * fy + br.g * fx * fy,
        b: tl.b * (1 - fx) * (1 - fy) + tr.b * fx * (1 - fy) + bl.b * (1 - fx) * fy + br.b * fx * fy,
    };
}

function makeImageDisplaceFn(params: DisplacementParams): DisplaceFn {
    const { imageData, strength, channelMode, svgMinX, svgMinY, svgWidth, svgHeight } = params;

    if (!imageData || svgWidth <= 0 || svgHeight <= 0) {
        return (x, y) => ({ x, y });
    }

    return (x: number, y: number) => {
        const u = (x - svgMinX) / svgWidth;
        const v = (y - svgMinY) / svgHeight;
        const { r, g, b } = sampleBilinear(imageData, u, v);

        let dx: number, dy: number;
        if (channelMode === "rg") {
            // Red channel → X displacement, Green channel → Y displacement.
            // 0.5 (mid-gray) = no displacement; 0 = negative, 1 = positive.
            dx = (r - 0.5) * 2 * strength;
            dy = (g - 0.5) * 2 * strength;
        } else {
            // Luminance mode: grayscale luminance drives both axes.
            // X and Y use the same magnitude but Y is inverted, producing
            // a swirling perpendicular-ish effect on grayscale images.
            const lum = 0.299 * r + 0.587 * g + 0.114 * b;
            dx =  (lum - 0.5) * 2 * strength;
            dy = -(lum - 0.5) * 2 * strength;
        }

        return { x: x + dx, y: y + dy };
    };
}

// ─── Dispatcher ─────────────────────────────────────────────────────────────

function makeDisplaceFn(params: DisplacementParams): DisplaceFn {
    return params.mode === "image"
        ? makeImageDisplaceFn(params)
        : makeNoiseDisplaceFn(params);
}

// ─── Attribute helpers ───────────────────────────────────────────────────────

function parseAttrs(attrString: string): Record<string, string> {
    const attrs: Record<string, string> = {};
    const regex = /(\w+(?:-\w+)*)=["']([^"']*?)["']/g;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(attrString)) !== null) {
        attrs[m[1]] = m[2];
    }
    return attrs;
}

function buildElement(tag: string, attrs: Record<string, string>): string {
    const parts = Object.entries(attrs).map(([k, v]) => `${k}="${v}"`);
    return `<${tag} ${parts.join(" ")} />`;
}

function fmt(n: number): string {
    return parseFloat(n.toFixed(3)).toString();
}

// ─── Polygon / polyline points ───────────────────────────────────────────────

function displacePolyPoints(points: string, displace: DisplaceFn): string {
    const nums = points.trim().split(/[\s,]+/).map(Number);
    const out: number[] = [];
    for (let i = 0; i + 1 < nums.length; i += 2) {
        const p = displace(nums[i], nums[i + 1]);
        out.push(p.x, p.y);
    }
    return out.map(fmt).join(" ");
}

// ─── Line ───────────────────────────────────────────────────────────────────

function displaceLineAttrs(
    attrs: Record<string, string>,
    displace: DisplaceFn,
): Record<string, string> {
    const p1 = displace(parseFloat(attrs.x1 ?? "0"), parseFloat(attrs.y1 ?? "0"));
    const p2 = displace(parseFloat(attrs.x2 ?? "0"), parseFloat(attrs.y2 ?? "0"));
    return { ...attrs, x1: fmt(p1.x), y1: fmt(p1.y), x2: fmt(p2.x), y2: fmt(p2.y) };
}

// ─── Rect → path ────────────────────────────────────────────────────────────

const PRESENTATION_ATTRS = [
    "fill", "stroke", "stroke-width", "opacity", "fill-opacity",
    "stroke-opacity", "stroke-dasharray", "stroke-linecap",
    "stroke-linejoin", "id", "class", "transform",
];

function rectToDisplacedPath(attrs: Record<string, string>, displace: DisplaceFn): string {
    const x = parseFloat(attrs.x ?? "0");
    const y = parseFloat(attrs.y ?? "0");
    const w = parseFloat(attrs.width ?? "0");
    const h = parseFloat(attrs.height ?? "0");
    const corners: [number, number][] = [
        [x, y], [x + w, y], [x + w, y + h], [x, y + h],
    ];
    const pts = corners.map(([cx, cy]) => displace(cx, cy));
    const d = `M${fmt(pts[0].x)},${fmt(pts[0].y)}` +
        pts.slice(1).map(p => `L${fmt(p.x)},${fmt(p.y)}`).join("") + "Z";
    const pathAttrs: Record<string, string> = { d };
    for (const k of PRESENTATION_ATTRS) {
        if (attrs[k] !== undefined) pathAttrs[k] = attrs[k];
    }
    return buildElement("path", pathAttrs);
}

// ─── Circle → path ──────────────────────────────────────────────────────────

function circleToDisplacedPath(attrs: Record<string, string>, displace: DisplaceFn, segments: number): string {
    const cx = parseFloat(attrs.cx ?? "0");
    const cy = parseFloat(attrs.cy ?? "0");
    const r  = parseFloat(attrs.r  ?? "0");
    const pts = Array.from({ length: segments }, (_, i) => {
        const angle = (2 * Math.PI * i) / segments;
        return displace(cx + r * Math.cos(angle), cy + r * Math.sin(angle));
    });
    const d = `M${fmt(pts[0].x)},${fmt(pts[0].y)}` +
        pts.slice(1).map(p => `L${fmt(p.x)},${fmt(p.y)}`).join("") + "Z";
    const pathAttrs: Record<string, string> = { d };
    for (const k of PRESENTATION_ATTRS) {
        if (attrs[k] !== undefined) pathAttrs[k] = attrs[k];
    }
    return buildElement("path", pathAttrs);
}

// ─── Ellipse → path ─────────────────────────────────────────────────────────

function ellipseToDisplacedPath(attrs: Record<string, string>, displace: DisplaceFn, segments: number): string {
    const cx = parseFloat(attrs.cx ?? "0");
    const cy = parseFloat(attrs.cy ?? "0");
    const rx = parseFloat(attrs.rx ?? "0");
    const ry = parseFloat(attrs.ry ?? "0");
    const pts = Array.from({ length: segments }, (_, i) => {
        const angle = (2 * Math.PI * i) / segments;
        return displace(cx + rx * Math.cos(angle), cy + ry * Math.sin(angle));
    });
    const d = `M${fmt(pts[0].x)},${fmt(pts[0].y)}` +
        pts.slice(1).map(p => `L${fmt(p.x)},${fmt(p.y)}`).join("") + "Z";
    const pathAttrs: Record<string, string> = { d };
    for (const k of PRESENTATION_ATTRS) {
        if (attrs[k] !== undefined) pathAttrs[k] = attrs[k];
    }
    return buildElement("path", pathAttrs);
}

// ─── Path d tokenizer + displacer ───────────────────────────────────────────

function tokenizePathD(d: string): Array<{ cmd: string; args: number[] }> {
    const tokens: Array<{ cmd: string; args: number[] }> = [];
    const parts = d.trim().match(/[MmZzLlHhVvCcSsQqTtAa][^MmZzLlHhVvCcSsQqTtAa]*/g);
    if (!parts) return tokens;
    for (const part of parts) {
        const cmd = part[0];
        const rest = part.slice(1).trim();
        const args = rest === "" ? [] : rest.split(/[\s,]+/).filter(Boolean).map(Number);
        tokens.push({ cmd, args });
    }
    return tokens;
}

export function displacePathD(d: string, displace: DisplaceFn): string {
    const tokens = tokenizePathD(d);
    const out: string[] = [];

    let cx = 0, cy = 0;
    let sx = 0, sy = 0;
    let lastCubicCp2x = 0, lastCubicCp2y = 0;
    let lastWasCubic = false;
    let lastQuadCpx = 0, lastQuadCpy = 0;
    let lastWasQuad = false;

    for (const { cmd, args } of tokens) {
        const isRel = cmd === cmd.toLowerCase() && cmd !== "Z" && cmd !== "z";
        const upper = cmd.toUpperCase();

        if (upper !== "C" && upper !== "S") lastWasCubic = false;
        if (upper !== "Q" && upper !== "T") lastWasQuad = false;

        switch (upper) {
            case "M": {
                for (let i = 0; i + 1 < args.length; i += 2) {
                    const ax = isRel ? cx + args[i]     : args[i];
                    const ay = isRel ? cy + args[i + 1] : args[i + 1];
                    const p = displace(ax, ay);
                    if (i === 0) { out.push(`M${fmt(p.x)},${fmt(p.y)}`); sx = ax; sy = ay; }
                    else           out.push(`L${fmt(p.x)},${fmt(p.y)}`);
                    cx = ax; cy = ay;
                }
                break;
            }
            case "Z": {
                out.push("Z");
                cx = sx; cy = sy;
                break;
            }
            case "L": {
                for (let i = 0; i + 1 < args.length; i += 2) {
                    const ax = isRel ? cx + args[i]     : args[i];
                    const ay = isRel ? cy + args[i + 1] : args[i + 1];
                    const p = displace(ax, ay);
                    out.push(`L${fmt(p.x)},${fmt(p.y)}`);
                    cx = ax; cy = ay;
                }
                break;
            }
            case "H": {
                for (let i = 0; i < args.length; i++) {
                    const ax = isRel ? cx + args[i] : args[i];
                    out.push(`L${fmt(displace(ax, cy).x)},${fmt(displace(ax, cy).y)}`);
                    cx = ax;
                }
                break;
            }
            case "V": {
                for (let i = 0; i < args.length; i++) {
                    const ay = isRel ? cy + args[i] : args[i];
                    out.push(`L${fmt(displace(cx, ay).x)},${fmt(displace(cx, ay).y)}`);
                    cy = ay;
                }
                break;
            }
            case "C": {
                for (let i = 0; i + 5 < args.length; i += 6) {
                    const x1 = isRel ? cx + args[i]     : args[i];
                    const y1 = isRel ? cy + args[i + 1] : args[i + 1];
                    const x2 = isRel ? cx + args[i + 2] : args[i + 2];
                    const y2 = isRel ? cy + args[i + 3] : args[i + 3];
                    const ax = isRel ? cx + args[i + 4] : args[i + 4];
                    const ay = isRel ? cy + args[i + 5] : args[i + 5];
                    const p1 = displace(x1, y1), p2 = displace(x2, y2), ep = displace(ax, ay);
                    out.push(`C${fmt(p1.x)},${fmt(p1.y)} ${fmt(p2.x)},${fmt(p2.y)} ${fmt(ep.x)},${fmt(ep.y)}`);
                    lastCubicCp2x = x2; lastCubicCp2y = y2; lastWasCubic = true;
                    cx = ax; cy = ay;
                }
                break;
            }
            case "S": {
                for (let i = 0; i + 3 < args.length; i += 4) {
                    const x2 = isRel ? cx + args[i]     : args[i];
                    const y2 = isRel ? cy + args[i + 1] : args[i + 1];
                    const ax = isRel ? cx + args[i + 2] : args[i + 2];
                    const ay = isRel ? cy + args[i + 3] : args[i + 3];
                    const rx1 = lastWasCubic ? 2 * cx - lastCubicCp2x : cx;
                    const ry1 = lastWasCubic ? 2 * cy - lastCubicCp2y : cy;
                    const p1 = displace(rx1, ry1), p2 = displace(x2, y2), ep = displace(ax, ay);
                    out.push(`C${fmt(p1.x)},${fmt(p1.y)} ${fmt(p2.x)},${fmt(p2.y)} ${fmt(ep.x)},${fmt(ep.y)}`);
                    lastCubicCp2x = x2; lastCubicCp2y = y2; lastWasCubic = true;
                    cx = ax; cy = ay;
                }
                break;
            }
            case "Q": {
                for (let i = 0; i + 3 < args.length; i += 4) {
                    const x1 = isRel ? cx + args[i]     : args[i];
                    const y1 = isRel ? cy + args[i + 1] : args[i + 1];
                    const ax = isRel ? cx + args[i + 2] : args[i + 2];
                    const ay = isRel ? cy + args[i + 3] : args[i + 3];
                    const p1 = displace(x1, y1), ep = displace(ax, ay);
                    out.push(`Q${fmt(p1.x)},${fmt(p1.y)} ${fmt(ep.x)},${fmt(ep.y)}`);
                    lastQuadCpx = x1; lastQuadCpy = y1; lastWasQuad = true;
                    cx = ax; cy = ay;
                }
                break;
            }
            case "T": {
                for (let i = 0; i + 1 < args.length; i += 2) {
                    const ax = isRel ? cx + args[i]     : args[i];
                    const ay = isRel ? cy + args[i + 1] : args[i + 1];
                    const rx1 = lastWasQuad ? 2 * cx - lastQuadCpx : cx;
                    const ry1 = lastWasQuad ? 2 * cy - lastQuadCpy : cy;
                    const p1 = displace(rx1, ry1), ep = displace(ax, ay);
                    out.push(`Q${fmt(p1.x)},${fmt(p1.y)} ${fmt(ep.x)},${fmt(ep.y)}`);
                    lastQuadCpx = rx1; lastQuadCpy = ry1; lastWasQuad = true;
                    cx = ax; cy = ay;
                }
                break;
            }
            case "A": {
                for (let i = 0; i + 6 < args.length; i += 7) {
                    const rx = args[i], ry = args[i + 1], rot = args[i + 2];
                    const la = args[i + 3], sw = args[i + 4];
                    const ax = isRel ? cx + args[i + 5] : args[i + 5];
                    const ay = isRel ? cy + args[i + 6] : args[i + 6];
                    const ep = displace(ax, ay);
                    out.push(`A${fmt(rx)},${fmt(ry)} ${fmt(rot)} ${la},${sw} ${fmt(ep.x)},${fmt(ep.y)}`);
                    cx = ax; cy = ay;
                }
                break;
            }
        }
    }

    return out.join(" ");
}

// ─── Element-level dispatcher ────────────────────────────────────────────────

function displaceElement(
    tag: string,
    attrString: string,
    fullMatch: string,
    displace: DisplaceFn,
    segments: number,
): string | null {
    const attrs = parseAttrs(attrString);
    switch (tag.toLowerCase()) {
        case "path": {
            const d = attrs.d;
            if (!d) return null;
            return buildElement("path", { ...attrs, d: displacePathD(d, displace) });
        }
        case "polygon": {
            const pts = attrs.points;
            if (!pts) return null;
            return buildElement("polygon", { ...attrs, points: displacePolyPoints(pts, displace) });
        }
        case "polyline": {
            const pts = attrs.points;
            if (!pts) return null;
            return buildElement("polyline", { ...attrs, points: displacePolyPoints(pts, displace) });
        }
        case "line":    return buildElement("line", displaceLineAttrs(attrs, displace));
        case "rect":    return rectToDisplacedPath(attrs, displace);
        case "circle":  return circleToDisplacedPath(attrs, displace, segments);
        case "ellipse": return ellipseToDisplacedPath(attrs, displace, segments);
        default:        return fullMatch;
    }
}

// ─── Entry point ─────────────────────────────────────────────────────────────

export function displaceSvgContent(svgContent: string, params: DisplacementParams): string {
    const { circleSegments, perShapeSeededNoise } = params;
    const shapeTagPattern = /(path|polygon|polyline|line|rect|circle|ellipse)/i;
    let shapeIndex = 0;

    return svgContent.replace(
        /<(path|polygon|polyline|line|rect|circle|ellipse)((?:[^>]|"[^"]*"|'[^']*')*?)\s*(?:\/>|>(?:<\/(?:path|polygon|polyline|line|rect|circle|ellipse)>)?)/gi,
        (fullMatch: string, tag: string, attrString: string) => {
            if (!shapeTagPattern.test(tag)) return fullMatch;
            const seed = perShapeSeededNoise
                ? `${params.seed}-s${shapeIndex}`
                : params.seed;
            shapeIndex += 1;
            const displace = makeDisplaceFn({ ...params, seed });
            return displaceElement(tag, attrString, fullMatch, displace, circleSegments) ?? fullMatch;
        },
    );
}

/**
 * Apply an arbitrary `DisplaceFn` to every supported shape in an SVG document
 * string, reusing the same element dispatch as `displaceSvgContent`. Lets
 * external warps (e.g. the lattice deformer) plug into the existing pipeline
 * without duplicating the path/rect/circle/etc rewriters.
 */
export function warpSvgWithFn(
    svgContent: string,
    displace: DisplaceFn,
    circleSegments: number = 48,
): string {
    const shapeTagPattern = /(path|polygon|polyline|line|rect|circle|ellipse)/i;
    return svgContent.replace(
        /<(path|polygon|polyline|line|rect|circle|ellipse)((?:[^>]|"[^"]*"|'[^']*')*?)\s*(?:\/>|>(?:<\/(?:path|polygon|polyline|line|rect|circle|ellipse)>)?)/gi,
        (fullMatch: string, tag: string, attrString: string) => {
            if (!shapeTagPattern.test(tag)) return fullMatch;
            return displaceElement(tag, attrString, fullMatch, displace, circleSegments) ?? fullMatch;
        },
    );
}
